import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CybersocXDRDashboardSearchCriteria } from '../shared/model/searchCriteria';
import { CybersocxdrService } from '../shared/services/xtendedsoc/cybersocxdr.service';
import { Utilities } from '../shared/utilities';
import { FilterEvent, FilterEventItem } from './components/filtermenu/filtermenu.component';

@Injectable({
  providedIn: 'root',
})
export class CybersocxdrglobalfiltersService {
  public searchCriteria: CybersocXDRDashboardSearchCriteria;

  public groupByNames: string[] = [];

  public repartitionLevels: [string, boolean][][] = undefined;
  public waitingFor: [string, boolean][] = [];
  public closureVerdict: [string, boolean][] = [];
  public categories: [string, boolean][] = [];
  public owners: [string, boolean][] = [];
  public severitys: [string, boolean][] = [
    ['Low', true],
    ['Medium', true],
    ['High', true],
    ['Critical', true],
  ];
  public status: [string, boolean][] = [];
  public createdFrom: string;
  public createdTo: string;
  public createdPeriod = 'lastmonth';

  private isInitialized = false;

  constructor(private cybersocxdrService: CybersocxdrService, private translate: TranslateService) {
    this.deserializeSearchCriteria();

    if (!this.searchCriteria) {
      this.searchCriteria = new CybersocXDRDashboardSearchCriteria();
      this.updateSearchCriteria();
      return;
    }

    this.resetFilter(this.severitys);
    this.resetFilter(this.status);
    this.searchCriteria.severitys.forEach((severity) => {
      this.severitys[parseInt(severity, 10) - 1][1] = true;
    });
    this.createdFrom = this.searchCriteria.createdFrom;
    this.createdTo = this.searchCriteria.createdTo;
  }

  async init() {
    if (this.isInitialized) {
      return;
    }
    this.isInitialized = true;
    const groupByTypes = await this.cybersocxdrService.getGroupByFilterTypes();
    for (let i = 0; i < groupByTypes.length; i++) {
      this.translate.get(`pages.cybersocxdr.filters.${groupByTypes[i]}`).subscribe((translated: string) => {
        this.groupByNames[i] = translated;
      });
    }

    const { repartitions, incidentStatus, waitingFor, closureVerdict, categories, owners } =
      await this.cybersocxdrService.getAdvancedFilterValues();

    if (repartitions !== null) {
      this.repartitionLevels = repartitions.map((x: string[]) => this.mapToToggleArray(x));
    }

    this.waitingFor = this.mapToToggleArray(waitingFor);
    this.status = this.mapToToggleArray(incidentStatus);
    this.closureVerdict = this.mapToToggleArray(closureVerdict);
    this.categories = this.mapToToggleArray(categories);
    this.owners = this.mapToToggleArray(owners);
  }

  addFilter(newFilter: FilterEvent, filterIn: boolean) {
    if (newFilter.primary.key) {
      // generic filter
      this.applyFilter(newFilter.primary, filterIn);
    } else {
      // handle repartition filtering
      if (filterIn && this.areAllRepartitionsSelected()) {
        // remove everything, filter back in right afte
        this.repartitionLevels?.forEach((level) => {
          level.forEach((filter) => {
            filter[1] = false;
          });
        });
      }

      this.repartitionLevels?.forEach((level) => {
        level.forEach((filter) => {
          if (filter[0] === newFilter.primary.value) {
            filter[1] = filterIn;
          }
        });
      });
    }
    if (newFilter.secondary) {
      this.applyFilter(newFilter.secondary, filterIn);
    }
    this.updateSearchCriteria();
  }

  applyFilter(newFilter: FilterEventItem, filterIn: boolean) {
    // time filter is special
    if (filterIn && newFilter.key === 'time') {
      [this.createdFrom, this.createdTo] = newFilter.value as [string, string];
      return;
    }

    if (
      filterIn ||
      this[newFilter.key].every(
        (keyValuePair: [string, boolean], index: number) => keyValuePair[1] === filterIn || index === newFilter.value
      )
    ) {
      this.resetFilter(this[newFilter.key], !filterIn);
    }
    this[newFilter.key][newFilter.value][1] = filterIn;
  }

  areAllRepartitionsSelected(): boolean {
    return this.repartitionLevels?.every((level) => level.every((filter) => filter[1] === true));
  }

  resetFilter(filter: [string, boolean][], resetValue = false) {
    filter.forEach((f) => {
      f[1] = resetValue;
    });
  }

  reset() {
    this.repartitionLevels?.forEach((level) => {
      this.resetFilter(level);
    });
    this.searchCriteria.groupLevel1 = [];
    this.searchCriteria.groupLevel2 = [];
    this.resetFilter(this.waitingFor);
    this.resetFilter(this.closureVerdict);
    this.resetFilter(this.categories);
    this.resetFilter(this.owners);
    this.resetFilter(this.severitys, true);
    this.resetFilter(this.status);
    this.createdPeriod = 'lastmonth';
    this.applyPeriod();
  }

  updateSearchCriteria() {
    this.applyPeriod();
    this.searchCriteria.groupLevel1 = this.getEnabledRepartitionFields(1);
    this.searchCriteria.groupLevel2 = this.getEnabledRepartitionFields(2);
    this.searchCriteria.severitys = this.getEnabledFieldsIndexes(this.severitys);
    this.searchCriteria.status = this.getEnabledFields(this.status);
    this.searchCriteria.category = this.getEnabledFields(this.categories);
    this.searchCriteria.waitingFor = this.getEnabledFields(this.waitingFor);
    this.searchCriteria.closureVerdict = this.getEnabledFields(this.closureVerdict);
    this.searchCriteria.owner = this.getEnabledFields(this.owners);
  }

  getEnabledRepartitionFields(level: number): string[] {
    if (!this.repartitionLevels) {
      return [];
    }
    return this.getEnabledFields(this.repartitionLevels[level - 1]);
  }

  getEnabledFields(array: [string, boolean][] | undefined): string[] {
    if (!array) {
      return [];
    }
    return array.filter((filter) => filter[1]).map((filter) => filter[0]);
  }

  // same but for fixed fields (severity, status)
  getEnabledFieldsIndexes(array: [string, boolean][] | undefined): string[] {
    if (!array) {
      return [];
    }
    return array.map((filter, index) => (filter[1] ? (index + 1).toString() : null)).filter((x) => x !== null);
  }

  // called after search is performed
  serializeSeachCriteria() {
    // we save only a subset of the search criteria
    const savedCriterias = (({ createdFrom, createdTo, status, severitys }) => ({
      createdFrom,
      createdTo,
      status,
      severitys,
    }))(this.searchCriteria);
    sessionStorage.setItem('xtended_soc_incident_form', JSON.stringify(savedCriterias));
  }

  applyPeriod() {
    if (this.createdPeriod) {
      if (this.createdPeriod === '1w') {
        this.createdFrom = Utilities.getTodayMinusDays(7);
        this.createdTo = Utilities.getTodayMinusDays(0);
      } else if (this.createdPeriod === '2w') {
        this.createdFrom = Utilities.getTodayMinusDays(14);
        this.createdTo = Utilities.getTodayMinusDays(0);
      } else if (this.createdPeriod === 'lastmonth') {
        this.createdFrom = Utilities.get1MonthAgo();
        this.createdTo = Utilities.getTodayMinusDays(0);
      } else if (this.createdPeriod === 'thismonth') {
        this.createdFrom = Utilities.getFirstOfCurrentMonth();
        this.createdTo = Utilities.getTodayMinusDays(0);
      } else if (this.createdPeriod === 'last6month') {
        this.createdFrom = Utilities.get6MonthsAgo();
        this.createdTo = Utilities.getTodayMinusDays(0);
      }
      this.searchCriteria.createdFrom = this.createdFrom;
      this.searchCriteria.createdTo = this.createdTo;
    }
  }

  private mapToToggleArray(values: string[]): [string, boolean][] {
    return values.map((x) => [x, false]);
  }

  /** Deserialize the search criteria data into form data */
  private deserializeSearchCriteria() {
    const storedItem = sessionStorage.getItem('xtended_soc_incident_form');
    if (storedItem) {
      this.searchCriteria = Object.assign(new CybersocXDRDashboardSearchCriteria(), JSON.parse(storedItem));
    }
  }
}
